/**
 * The paste progress total — "N items" for the progress toast shown while a copy
 * runs. It is a progress figure, never a gate, so the copy must not sit waiting
 * on it: folders are counted with a SHORT server-side budget
 * (`/count?budgetMs=`), a few at a time.
 *
 * At most `PASTE_COUNT_CONCURRENCY` counts are in flight: each `/count` walk
 * holds a pooled connection per step for up to its budget, so an unbounded
 * fan-out over a big selection would crowd the pool. (A mount-child `/count` no
 * longer holds a scoped connection while it walks — it runs outside the tenant
 * scope, own-scope.ts — so the 2N-connection class is closed server-side; the
 * cap stays as ordinary back-pressure.)
 *
 * `truncated` = at least one count was cut short (only a lower bound): the
 * caller shows an INDETERMINATE progress rather than a total that can overshoot.
 */
export const PASTE_COUNT_BUDGET_MS = 300
export const PASTE_COUNT_CONCURRENCY = 3

export async function pasteProgressTotal(
  items: ReadonlyArray<{ id: string; isFolder: boolean }>,
  countChildren: (folderId: string, opts: { budgetMs: number }) => Promise<{ count: number; truncated: boolean }>,
): Promise<{ total: number; truncated: boolean }> {
  const parts: Array<{ count: number; truncated: boolean }> = new Array(items.length)
  let next = 0
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const i = next++
      const item = items[i]
      if (!item.isFolder) { parts[i] = { count: 1, truncated: false }; continue }
      // The count includes the root folder itself; a failed count is unknown.
      try { parts[i] = await countChildren(item.id, { budgetMs: PASTE_COUNT_BUDGET_MS }) } catch { parts[i] = { count: 1, truncated: true } }
    }
  }
  await Promise.all(Array.from({ length: Math.min(PASTE_COUNT_CONCURRENCY, items.length) }, worker))
  return {
    total: parts.reduce((a, p) => a + p.count, 0),
    truncated: parts.some((p) => p.truncated),
  }
}
